/**
 * BackgroundMusicSection - Video-level background music control
 *
 * Features:
 * - Collapsible section with music icon
 * - Three input modes: Search (Freesound), Upload file, Paste URL
 * - Selected track display with preview playback
 * - Volume slider (0-100%)
 * - Loop toggle
 *
 * This is video-level music that plays across all slides,
 * separate from per-slide narrator audio.
 */

import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import {
  Music,
  ChevronDown,
  ChevronRight,
  VolumeX,
  Play,
  Pause,
  Repeat,
  X,
  Upload,
  Link,
  Loader2,
  Check,
  AlertCircle,
  Mic2,
} from 'lucide-react';
import { useScopedBackgroundMusic } from '@/stores/videoWorkspaceStore';
import { BackgroundMusicModal, type MusicConfig } from './BackgroundMusicModal';
import { backgroundMusicAPI, videoManifestAPI } from '@/services/api';

type InputMode = 'upload' | 'url';

interface BackgroundMusicSectionProps {
  conversationId: string;
  onMusicChange?: (config: {
    url: string;
    volume: number;
    loop: boolean;
  } | null) => void;
  onNarratorVolumeChange?: (volume: number) => void;
  className?: string;
}

export const BackgroundMusicSection: React.FC<BackgroundMusicSectionProps> = ({
  conversationId,
  onMusicChange,
  onNarratorVolumeChange,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // URL input state
  const [urlInput, setUrlInput] = useState('');
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const previewAudioRef = useRef<HTMLAudioElement | null>(null);

  const {
    backgroundMusic,
    setBackgroundMusic,
    updateVolume,
    updateLoop,
    updateNarratorVolume,
    removeBackgroundMusic,
  } = useScopedBackgroundMusic(conversationId);

  // Initialize audio element for preview
  useEffect(() => {
    previewAudioRef.current = new Audio();
    previewAudioRef.current.addEventListener('ended', () => {
      setIsPreviewPlaying(false);
    });

    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.src = '';
      }
    };
  }, []);

  // Update preview audio when track changes
  useEffect(() => {
    if (previewAudioRef.current && backgroundMusic?.track?.url) {
      previewAudioRef.current.src = backgroundMusic.track.url;
      previewAudioRef.current.volume = backgroundMusic.volume;
      previewAudioRef.current.loop = backgroundMusic.loop;
    }
  }, [backgroundMusic?.track?.url, backgroundMusic?.volume, backgroundMusic?.loop]);

  // Notify parent when music config changes
  useEffect(() => {
    if (onMusicChange) {
      if (backgroundMusic?.track) {
        onMusicChange({
          url: backgroundMusic.track.url,
          volume: backgroundMusic.volume,
          loop: backgroundMusic.loop,
        });
      } else {
        onMusicChange(null);
      }
    }
  }, [backgroundMusic, onMusicChange]);

  // Handle music selection from modal
  const handleApplyMusic = useCallback(async (config: MusicConfig) => {
    // Update local state
    setBackgroundMusic(conversationId, {
      id: config.track.id,
      title: config.track.title,
      url: config.track.url,
      duration: config.track.duration,
      source: config.track.source === 'library' ? 'freesound' : config.track.source,
    });
    updateVolume(conversationId, config.volume);
    updateLoop(conversationId, config.loop);
    setInputMode(null);

    // Save to video manifest
    try {
      await videoManifestAPI.updateBackgroundMusic(conversationId, {
        url: config.track.url,
        volume: config.volume,
        loop: config.loop,
      });
      console.log('[BackgroundMusicSection] Saved background music to manifest');
    } catch (error) {
      console.error('[BackgroundMusicSection] Failed to save to manifest:', error);
      // Don't throw - local state is already updated for immediate preview
    }
  }, [conversationId, setBackgroundMusic, updateVolume, updateLoop]);

  // Handle file upload
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const result = await backgroundMusicAPI.upload(conversationId, file);

      setBackgroundMusic(conversationId, {
        id: `uploaded-${Date.now()}`,
        title: result.filename,
        url: result.url,
        duration: 0, // Unknown until played
        source: 'uploaded',
      });
      setInputMode(null);
    } catch (error) {
      console.error('[BackgroundMusic] Upload failed:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [conversationId, setBackgroundMusic]);

  // Handle URL submit
  const handleUrlSubmit = useCallback(async () => {
    if (!urlInput.trim()) return;

    setIsValidatingUrl(true);
    setUrlError(null);

    try {
      // Basic URL validation
      const url = new URL(urlInput.trim());

      // Optionally validate the URL points to audio
      const validation = await backgroundMusicAPI.validateUrl(url.toString());

      if (!validation.valid) {
        // Still allow it, but warn
        console.warn('[BackgroundMusic] URL may not be audio:', validation.error);
      }

      setBackgroundMusic(conversationId, {
        id: `url-${Date.now()}`,
        title: url.pathname.split('/').pop() || 'Custom Music',
        url: url.toString(),
        duration: 0,
        source: 'url',
      });
      setUrlInput('');
      setInputMode(null);
    } catch (error) {
      console.error('[BackgroundMusic] Invalid URL:', error);
      setUrlError('Please enter a valid URL');
    } finally {
      setIsValidatingUrl(false);
    }
  }, [urlInput, conversationId, setBackgroundMusic]);

  const handleVolumeChange = useCallback(async (values: number[]) => {
    const volume = values[0] / 100;
    updateVolume(conversationId, volume);
    if (previewAudioRef.current) {
      previewAudioRef.current.volume = volume;
    }

    // Persist to manifest if there's a track applied
    if (backgroundMusic?.track?.url && backgroundMusic?.isApplied) {
      try {
        await videoManifestAPI.updateBackgroundMusic(conversationId, {
          url: backgroundMusic.track.url,
          volume,
          loop: backgroundMusic.loop ?? true,
        });
      } catch (error) {
        console.error('[BackgroundMusicSection] Failed to save music volume:', error);
      }
    }
  }, [conversationId, updateVolume, backgroundMusic?.track?.url, backgroundMusic?.isApplied, backgroundMusic?.loop]);

  const handleLoopToggle = useCallback(async (checked: boolean) => {
    updateLoop(conversationId, checked);
    if (previewAudioRef.current) {
      previewAudioRef.current.loop = checked;
    }

    // Persist to manifest if there's a track applied
    if (backgroundMusic?.track?.url && backgroundMusic?.isApplied) {
      try {
        await videoManifestAPI.updateBackgroundMusic(conversationId, {
          url: backgroundMusic.track.url,
          volume: backgroundMusic.volume ?? 0.3,
          loop: checked,
        });
      } catch (error) {
        console.error('[BackgroundMusicSection] Failed to save loop setting:', error);
      }
    }
  }, [conversationId, updateLoop, backgroundMusic?.track?.url, backgroundMusic?.isApplied, backgroundMusic?.volume]);

  const handleNarratorVolumeChange = useCallback(async (values: number[]) => {
    const volume = values[0] / 100;
    updateNarratorVolume(conversationId, volume);

    // Notify parent to sync to iframe
    if (onNarratorVolumeChange) {
      onNarratorVolumeChange(volume);
    }

    // Save to manifest
    try {
      await videoManifestAPI.updateAudioMix(conversationId, { narratorVolume: volume });
    } catch (error) {
      console.error('[BackgroundMusicSection] Failed to save narrator volume:', error);
    }
  }, [conversationId, updateNarratorVolume, onNarratorVolumeChange]);

  const handleRemoveMusic = useCallback(async () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      setIsPreviewPlaying(false);
    }
    removeBackgroundMusic(conversationId);

    // Remove from video manifest
    try {
      await videoManifestAPI.updateBackgroundMusic(conversationId, null);
      console.log('[BackgroundMusicSection] Removed background music from manifest');
    } catch (error) {
      console.error('[BackgroundMusicSection] Failed to remove from manifest:', error);
    }
  }, [conversationId, removeBackgroundMusic]);

  const togglePreview = useCallback(() => {
    if (!previewAudioRef.current || !backgroundMusic?.track?.url) return;

    if (isPreviewPlaying) {
      previewAudioRef.current.pause();
      setIsPreviewPlaying(false);
    } else {
      previewAudioRef.current.play().catch(console.error);
      setIsPreviewPlaying(true);
    }
  }, [isPreviewPlaying, backgroundMusic?.track?.url]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const volumePercent = Math.round((backgroundMusic?.volume ?? 0.3) * 100);
  const narratorVolumePercent = Math.round((backgroundMusic?.narratorVolume ?? 1.0) * 100);
  const hasTrack = !!backgroundMusic?.track;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn("border-t border-border/50", className)}
    >
      {/* Header */}
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            "w-full flex items-center justify-between px-3 py-2.5",
            "hover:bg-muted/50 transition-colors",
            "text-left"
          )}
        >
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium">Background Music</span>
            {hasTrack && (
              <Badge variant="secondary" className="text-[9px] px-1.5 py-0">
                Active
              </Badge>
            )}
          </div>
          {isOpen ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>

      {/* Content */}
      <CollapsibleContent className="px-3 pb-3 space-y-3">
        {/* Add Music Button (when no track) */}
        {!hasTrack && inputMode === null && (
          <div className="space-y-3">
            {/* Main Add Music Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className={cn(
                "w-full h-14 flex-col gap-1.5",
                "border-dashed hover:border-primary/50 hover:bg-primary/5"
              )}
            >
              <Music className="w-5 h-5" />
              <span className="text-xs font-medium">Add Background Music</span>
            </Button>

            {/* Secondary Options */}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
              >
                <Upload className="w-3 h-3 inline mr-1" />
                Upload file
              </button>
              <span className="text-muted-foreground/40">|</span>
              <button
                onClick={() => setInputMode('url')}
                className="text-[10px] text-muted-foreground hover:text-primary transition-colors"
              >
                <Link className="w-3 h-3 inline mr-1" />
                Paste URL
              </button>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Upload Mode (inline) */}
        {inputMode === 'upload' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-muted-foreground">Upload Music</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setInputMode(null)}
                className="h-5 px-1.5 text-[10px]"
              >
                Cancel
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5 mr-2" />
                  Choose Audio File
                </>
              )}
            </Button>
            {uploadError && (
              <p className="text-[10px] text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {uploadError}
              </p>
            )}
          </div>
        )}

        {/* URL Mode */}
        {inputMode === 'url' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-muted-foreground">Paste URL</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setInputMode(null);
                  setUrlInput('');
                  setUrlError(null);
                }}
                className="h-5 px-1.5 text-[10px]"
              >
                Cancel
              </Button>
            </div>
            <div className="flex gap-1.5">
              <Input
                type="url"
                placeholder="https://example.com/music.mp3"
                value={urlInput}
                onChange={(e) => {
                  setUrlInput(e.target.value);
                  setUrlError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUrlSubmit();
                }}
                className="h-8 text-xs flex-1"
              />
              <Button
                size="sm"
                onClick={handleUrlSubmit}
                disabled={!urlInput.trim() || isValidatingUrl}
                className="h-8 px-2"
              >
                {isValidatingUrl ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
            {urlError && (
              <p className="text-[10px] text-destructive flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {urlError}
              </p>
            )}
            <p className="text-[9px] text-muted-foreground/60">
              Direct link to MP3, WAV, or other audio file
            </p>
          </div>
        )}

        {/* Uploading indicator (shown regardless of mode) */}
        {isUploading && inputMode === null && (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Uploading...</span>
          </div>
        )}

        {/* Hidden file input (accessible from any mode) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Selected Track Display */}
        {hasTrack && (
          <div className="space-y-3">
            {/* Track Info Card */}
            <div className={cn(
              "relative p-2.5 rounded-lg",
              "bg-gradient-to-br from-primary/5 to-primary/10",
              "border border-primary/20"
            )}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Music className="w-3 h-3 text-primary" />
                    <span className="text-[10px] font-medium text-primary/80 uppercase">
                      {backgroundMusic.track?.source === 'freesound' ? 'Freesound' :
                       backgroundMusic.track?.source === 'uploaded' ? 'Uploaded' : 'Custom URL'}
                    </span>
                  </div>
                  <p className="text-xs font-medium truncate" title={backgroundMusic.track?.title}>
                    {backgroundMusic.track?.title}
                  </p>
                  {backgroundMusic.track?.duration && backgroundMusic.track.duration > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Duration: {formatDuration(backgroundMusic.track.duration)}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {/* Preview Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6"
                    onClick={togglePreview}
                    title={isPreviewPlaying ? 'Pause preview' : 'Play preview'}
                  >
                    {isPreviewPlaying ? (
                      <Pause className="w-3 h-3" />
                    ) : (
                      <Play className="w-3 h-3" />
                    )}
                  </Button>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-6 h-6 text-muted-foreground hover:text-destructive"
                    onClick={handleRemoveMusic}
                    title="Remove music"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Change Track Options */}
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="text-[10px] text-primary/70 hover:text-primary hover:underline"
                >
                  Change
                </button>
                <span className="text-[10px] text-muted-foreground/50">|</span>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-[10px] text-primary/70 hover:text-primary hover:underline"
                >
                  Upload
                </button>
                <span className="text-[10px] text-muted-foreground/50">|</span>
                <button
                  onClick={() => setInputMode('url')}
                  className="text-[10px] text-primary/70 hover:text-primary hover:underline"
                >
                  URL
                </button>
              </div>
            </div>

            {/* Audio Mix Section */}
            <div className="space-y-3">
              <div className="text-[10px] font-medium text-muted-foreground border-b border-border/30 pb-1">
                Audio Mix
              </div>

              {/* Narrator Volume */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1.5">
                    <Mic2 className="w-3 h-3" />
                    Narrator
                  </Label>
                  <span className="text-[10px] font-medium text-foreground tabular-nums">
                    {narratorVolumePercent}%
                  </span>
                </div>
                <Slider
                  value={[narratorVolumePercent]}
                  onValueChange={handleNarratorVolumeChange}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Music Volume */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1.5">
                    {volumePercent === 0 ? (
                      <VolumeX className="w-3 h-3" />
                    ) : (
                      <Music className="w-3 h-3" />
                    )}
                    Music
                  </Label>
                  <span className="text-[10px] font-medium text-foreground tabular-nums">
                    {volumePercent}%
                  </span>
                </div>
                <Slider
                  value={[volumePercent]}
                  onValueChange={handleVolumeChange}
                  max={100}
                  step={1}
                  className="w-full"
                />
              </div>

              <p className="text-[9px] text-muted-foreground/70">
                Adjust narrator vs music balance
              </p>
            </div>

            {/* Loop Toggle */}
            <div className="flex items-center justify-between">
              <Label
                htmlFor="loop-toggle"
                className="text-[10px] font-medium text-muted-foreground flex items-center gap-1.5 cursor-pointer"
              >
                <Repeat className="w-3 h-3" />
                Loop music
              </Label>
              <Switch
                id="loop-toggle"
                checked={backgroundMusic?.loop ?? true}
                onCheckedChange={handleLoopToggle}
                className="scale-75 origin-right"
              />
            </div>
          </div>
        )}

        {/* Info Text */}
        {!hasTrack && inputMode === null && (
          <p className="text-[9px] text-muted-foreground/60 leading-relaxed">
            Add background music that plays across all slides at a lower volume behind narration.
          </p>
        )}
      </CollapsibleContent>

      {/* Background Music Modal */}
      <BackgroundMusicModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        initialConfig={
          backgroundMusic?.track
            ? {
                track: {
                  id: backgroundMusic.track.id,
                  title: backgroundMusic.track.title,
                  url: backgroundMusic.track.url,
                  duration: backgroundMusic.track.duration,
                  source: backgroundMusic.track.source as 'library' | 'freesound' | 'uploaded' | 'url',
                },
                volume: backgroundMusic.volume,
                loop: backgroundMusic.loop,
              }
            : null
        }
        onApply={handleApplyMusic}
      />
    </Collapsible>
  );
};

export default BackgroundMusicSection;
